import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Plus, Edit, Trash2, Store, Clock } from 'lucide-react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { api } from '../../lib/auth';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    department_id: '',
    title: '',
    short_description: '',
    tags: '',
    sla_estimate_days: 5
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, deptsRes] = await Promise.all([
        api.getServices(),
        api.getDepartments()
      ]);
      setServices(servicesRes.data);
      setDepartments(deptsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateService = async () => {
    if (!formData.department_id || !formData.title) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await api.createService({
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        sla_estimate_days: parseInt(formData.sla_estimate_days)
      });
      toast.success('Service created');
      setDialogOpen(false);
      setFormData({
        department_id: '',
        title: '',
        short_description: '',
        tags: '',
        sla_estimate_days: 5
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create service');
    }
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = !search || 
      service.title.toLowerCase().includes(search.toLowerCase()) ||
      service.short_description?.toLowerCase().includes(search.toLowerCase());
    const matchesDept = !selectedDept || selectedDept === 'all' || service.department_id === selectedDept;
    return matchesSearch && matchesDept;
  });

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">Manage Services</h1>
            <p className="text-gray-400">{services.length} services across {departments.length} departments</p>
          </div>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="btn-primary"
            data-testid="add-service-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </Button>
        </motion.div>

        {/* Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-xl p-4 mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type="text"
                placeholder="Search services..."
                className="pl-10 input-dark"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={selectedDept} onValueChange={setSelectedDept}>
              <SelectTrigger className="w-full sm:w-56 input-dark">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10 max-h-60">
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-40 bg-white/5 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-16">
              <Store className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No services found</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredServices.map((service, i) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="glass rounded-xl p-4"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="px-2 py-1 rounded-md bg-white/5 text-xs text-gray-400">
                      {service.department_name || 'Department'}
                    </span>
                    {service.covered_by_coop && (
                      <span className="px-2 py-1 rounded-md bg-green-500/20 text-xs text-green-400">
                        Co-Op
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-white mb-1 line-clamp-1">{service.title}</h3>
                  <p className="text-sm text-gray-400 line-clamp-2 mb-3">
                    {service.short_description}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1 text-gray-500">
                      <Clock className="w-4 h-4" />
                      {service.sla_estimate_days} days
                    </span>
                    <span className="text-primary text-xs">
                      Price on Demand
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Add Service Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Service</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Department *</label>
                <Select 
                  value={formData.department_id} 
                  onValueChange={(value) => setFormData({ ...formData, department_id: value })}
                >
                  <SelectTrigger className="input-dark">
                    <SelectValue placeholder="Select a department" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10 max-h-60">
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Service Title *</label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Landing Page Design"
                  className="input-dark"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Short Description</label>
                <Textarea
                  value={formData.short_description}
                  onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                  placeholder="Brief description of the service..."
                  className="input-dark min-h-[80px]"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Tags (comma-separated)</label>
                <Input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g., Design, Conversion, Landing Page"
                  className="input-dark"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Estimated Delivery (days)</label>
                <Input
                  type="number"
                  value={formData.sla_estimate_days}
                  onChange={(e) => setFormData({ ...formData, sla_estimate_days: e.target.value })}
                  className="input-dark"
                />
              </div>

              <div className="pt-4 flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateService}
                  className="flex-1 btn-primary"
                  data-testid="create-service-btn"
                >
                  Create Service
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
